import { useState, useEffect } from 'react';
import { X, User, Calendar, Phone, Mail, Clock, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import type { PatientWithDoctors } from '@/services/api/patients.service';
import type { Appointment } from '@/types';
import { appointmentsService } from '@/services/api/appointments.service';

interface PatientInfoPanelProps {
  patient: PatientWithDoctors;
  onClose: () => void;
}

export function PatientInfoPanel({ patient, onClose }: PatientInfoPanelProps) {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'info' | 'history'>('info');

  useEffect(() => {
    loadPatientAppointments();
  }, [patient.id]);

  const loadPatientAppointments = async () => {
    try {
      setLoading(true);
      const response = await appointmentsService.getAll({
        patientId: patient.id,
        limit: 50,
      });
      const sorted = response.data.sort(
        (a, b) => new Date(b.startUtc).getTime() - new Date(a.startUtc).getTime()
      );
      setAppointments(sorted);
    } catch (error: any) {
      toast.error('Erro ao carregar histórico de consultas');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCPF = (cpf?: string | null) => {
    if (!cpf) return '-';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (phone?: string | null) => {
    if (!phone) return '-';
    return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  };

  const calculateAge = (birthDate?: string | null) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const age = calculateAge(patient.birthDate);

  // Separate past and future appointments
  const now = new Date();
  const pastAppointments = appointments.filter((apt) => new Date(apt.endUtc) < now);
  const futureAppointments = appointments.filter((apt) => new Date(apt.startUtc) >= now);

  return (
    <div className="fixed right-0 top-0 bottom-0 w-96 bg-white shadow-2xl z-50 flex flex-col">
      {/* Header */}
      <div className="bg-blue-600 text-white p-4 flex items-center justify-between">
        <div className="flex items-center">
          <User className="w-6 h-6 mr-2" />
          <h2 className="text-lg font-bold">Informações do Paciente</h2>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-blue-700 rounded transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b">
        <button
          onClick={() => setActiveTab('info')}
          className={`flex-1 px-4 py-3 font-medium ${
            activeTab === 'info'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Dados
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`flex-1 px-4 py-3 font-medium ${
            activeTab === 'history'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Histórico
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'info' ? (
          <div className="space-y-4">
            {/* Patient Name */}
            <div>
              <h3 className="text-xl font-bold text-gray-900">{patient.fullName}</h3>
              <div className={`inline-block mt-1 px-2 py-1 rounded-full text-xs ${
                patient.isActive
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {patient.isActive ? 'Ativo' : 'Inativo'}
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-3">
              {patient.email && (
                <div className="flex items-center text-sm">
                  <Mail className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-gray-700">{patient.email}</span>
                </div>
              )}
            </div>

            {/* Personal Info */}
            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">CPF:</span>
                <span className="font-medium">{formatCPF(patient.cpf)}</span>
              </div>
              {patient.birthDate && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Data de Nascimento:</span>
                  <span className="font-medium">{formatDate(patient.birthDate)}</span>
                </div>
              )}
              {age !== null && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Idade:</span>
                  <span className="font-medium">{age} anos</span>
                </div>
              )}
            </div>

            {/* Health Info */}
            {(patient.weightKg || patient.heightCm || patient.bmi) && (
              <div className="border-t pt-4 space-y-2">
                {patient.weightKg && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Peso:</span>
                    <span className="font-medium">{patient.weightKg} kg</span>
                  </div>
                )}
                {patient.heightCm && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Altura:</span>
                    <span className="font-medium">{patient.heightCm} cm</span>
                  </div>
                )}
                {patient.bmi && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">IMC:</span>
                    <span className="font-medium">{patient.bmi}</span>
                  </div>
                )}
              </div>
            )}

            {/* Insurance Info */}
            <div className="border-t pt-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Convênio:</span>
                <span className="font-medium">
                  {patient.isInsurance ? 'Sim' : 'Particular'}
                </span>
              </div>
              {patient.isInsurance && patient.insuranceProvider && (
                <div className="text-sm text-gray-700 ml-4">
                  {patient.insuranceProvider}
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="border-t pt-4 grid grid-cols-2 gap-4">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {futureAppointments.length}
                </div>
                <div className="text-xs text-blue-600 mt-1">Próximas Consultas</div>
              </div>
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {pastAppointments.length}
                </div>
                <div className="text-xs text-green-600 mt-1">Consultas Realizadas</div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Future Appointments */}
            {futureAppointments.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  Próximas Consultas
                </h4>
                <div className="space-y-2">
                  {futureAppointments.map((apt) => (
                    <div
                      key={apt.id}
                      className="bg-blue-50 border border-blue-200 rounded-lg p-3"
                    >
                      <div className="font-medium text-blue-900">{apt.title}</div>
                      <div className="text-sm text-blue-700 mt-1">
                        {formatDateTime(apt.startUtc)}
                      </div>
                      {apt.description && (
                        <div className="text-xs text-blue-600 mt-2">
                          {apt.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Past Appointments */}
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : pastAppointments.length > 0 ? (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  Histórico de Consultas
                </h4>
                <div className="space-y-2">
                  {pastAppointments.map((apt) => (
                    <div
                      key={apt.id}
                      className="bg-gray-50 border border-gray-200 rounded-lg p-3"
                    >
                      <div className="font-medium text-gray-900">{apt.title}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        {formatDateTime(apt.startUtc)}
                      </div>
                      {apt.description && (
                        <div className="text-xs text-gray-500 mt-2">
                          {apt.description}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Nenhuma consulta anterior</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
